import * as React from 'react';
import pluralize from 'pluralize';
import { useState, useEffect } from 'react';
import { enqueueSnackbar } from 'notistack';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import { Divider } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Grid from '@mui/material/Unstable_Grid2';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Accordion from '@mui/material/Accordion';
import CakeIcon from '@mui/icons-material/Cake';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TranslateIcon from '@mui/icons-material/Translate';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';

import QuestionMarkInfoPopup from 'src/universal-components/question-mark-info-popup';

import ReferralCards from './referral-cards';
import Label from '../../../../components/label';
import { useBoolean } from '../../../../hooks/use-boolean';
import { lgbtqiaIdentity } from '../../../../assets/data/lgbtqia-identity';
import { Provider, ScoredProvider } from '../../../../types/newTypes/provider';
import FormCommentsSection from '../../../../universal-components/comments/form-comments-section';
import { useDjangoApi } from '../../../../universal-components/djangoServerUtilities/djangoHandler';

// ----------------------------------------------------------------------

type Props = {
  scoredProvider: ScoredProvider;
  selectedSpecialties: string[];
  selectedDemographics: string[];
  clientID: string;
  selectedState: string;
};

export default function MatchingByClientTableRow({ scoredProvider, selectedSpecialties, selectedDemographics, clientID, selectedState }: Props) {
  const displayModal = useBoolean();
  const djangoApi = useDjangoApi();
  const theme = useTheme();
  const [providerDemographics, setProviderDemographics] = useState<any[]>([]);
  const [provider, setProvider] = useState<Provider | undefined>(undefined)
  const hasLoaded = useBoolean(false);

  useEffect(() => {
    if (provider !== undefined) {
      let demographics: string[] = [];
      if (provider.race_and_ethnicity)
        demographics = demographics.concat(provider.race_and_ethnicity);
      if (provider.gender_identity)
        demographics.push(provider.gender_identity)
      if (provider.lgbtqia_identity) {
        // this just maps identities from their values to their labels
        provider.lgbtqia_identity.map((providerIdentity) => demographics.push(lgbtqiaIdentity.find((identity) => providerIdentity === identity.value)?.label as string))
      }
      if (provider.religion_and_spirituality)
        demographics = demographics.concat(provider.religion_and_spirituality)

      // not listed is not helpful
      setProviderDemographics(demographics.filter((element) => element !== 'Not Listed'));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider])

  const onError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    e.preventDefault();
    e.currentTarget.src = 'https://storage.googleapis.com/unify-mental-health/No_Photo_Available.jpg'
    e.currentTarget.onerror = null;
  }

  const fetchProviderData = async () => {
    const response = await djangoApi.get(`/providers/${scoredProvider.provider_id}`);
    if (response && response.status === 200) {
      setProvider(response.data)
      hasLoaded.onTrue()
    } else {
      enqueueSnackbar("Error fetching provider data", { variant: 'error' });
    }
  }

  useEffect(() => {
    if (displayModal.value) {
      fetchProviderData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayModal.value]);

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {scoredProvider.name}
          {scoredProvider.previously_matched && <Label color='error' sx={{ ml: 1 }}>Previously Matched</Label>}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{scoredProvider.score}%</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{scoredProvider.oldest_referral_request ? scoredProvider.oldest_referral_request.split("T")[0] : 'N/A'}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{pluralize(`${scoredProvider.caseload} client`, Number(scoredProvider.caseload))}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <SearchIcon onClick={() => {
            displayModal.onTrue();
          }
          }
            sx={{
              color: '#637381',
              cursor: 'pointer'
            }}
          />
        </TableCell>
      </TableRow>

      <Dialog
        open={displayModal.value}
        onClose={displayModal.onFalse}
        PaperProps={{
          style: {
            width: '1150px',
            maxWidth: '1150px',
            height: '85vh',
          },
        }}
      >
        {
          hasLoaded.value && provider !== undefined ?
            <DialogContent sx={{ height: 'auto', mt: 3 }}>
              {/* Not my favorite fix ever, but this box and its padding do get rid of the scrollbar when it isn't needed */}
              <Stack
                direction="row"
                spacing={2}
                divider={<Divider orientation="vertical" flexItem />}
              >
                {/* 108 is the height of the dialog actions plus the height of the margin up top (mt:3) */}
                <Box sx={{ height: 'calc(85vh - 108px)', maxWidth: '700px', overflowY: 'scroll', scrollbarWidth: 'none' }}>
                  {/* the styling for readonly, disabled fields has to be manually applied here since we can't use RHFTextField on its own */}
                  <Grid container spacing={3}>
                    <Grid xs={12}>
                      <Stack
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                        sx={{ height: '350px', pt: 3 }}
                      >
                        <Box sx={{
                          height: '300px',
                          width: '300px',
                          overflow: 'hidden',
                          borderRadius: '15px',
                          boxShadow: 10,
                          position: 'relative'
                        }}>
                          <img
                            src={`https://storage.googleapis.com/unify_public/provider_profile_pictures/${scoredProvider.provider_id}.jpg?cache_bust=${new Date().getTime()}`}
                            alt="profilePicture"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '15px'
                            }}
                            onError={(e) => { onError(e) }}
                          />
                        </Box>
                      </Stack>
                    </Grid>
                    <Grid xs={12} sx={{
                      textAlign: 'center',
                      mb: 4,
                      px: 2
                    }}>
                      <Typography
                        variant="h4"
                        sx={{
                          mb: 1,
                          fontWeight: 600,
                          color: theme.palette.text.primary
                        }}
                      >
                        {provider.display_name}
                      </Typography>

                      <Typography
                        sx={{
                          mb: 0.5,
                          color: theme.palette.text.secondary,
                          fontSize: '1.1rem'
                        }}
                      >
                        {/* {provider.license.fullName} */} License Name
                      </Typography>
                      <Typography
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '1rem'
                        }}
                      >
                        {/* {provider.practiceName} */} PracticeName
                      </Typography>
                    </Grid>
                  </Grid>

                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h6">Bio</Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 2,
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: theme.shadows[4],
                          },
                        }}
                      >
                        <Grid container spacing={3}>
                          <Grid xs={12} md={6}>
                            <TextField
                              fullWidth
                              disabled
                              label="License #"
                              // value={`${provider.license.licenseNumber ? provider.license.licenseNumber : 'N/A'}`}
                              value='LicenseNumber'
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: theme.palette.grey[50],
                                  borderRadius: 2,
                                  '&.Mui-disabled': {
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: theme.palette.divider,
                                    },
                                    '& .MuiInputLabel-root': {
                                      color: theme.palette.text.secondary,
                                    },
                                  },
                                },
                                '& .MuiInputLabel-root': {
                                  color: theme.palette.text.primary,
                                },
                              }}
                            />
                          </Grid>
                          <Grid xs={12} md={6}>
                            <TextField
                              fullWidth
                              disabled
                              label="License Expiration Date"
                              // value={`${provider.license.licenseExpiration ? provider.license.licenseExpiration : 'N/A'}`}
                              value='License Expiration Date'
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: theme.palette.grey[50],
                                  borderRadius: 2,
                                  '&.Mui-disabled': {
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: theme.palette.divider,
                                    },
                                    '& .MuiInputLabel-root': {
                                      color: theme.palette.text.secondary,
                                    },
                                  },
                                },
                                '& .MuiInputLabel-root': {
                                  color: theme.palette.text.primary,
                                },
                              }}
                            />
                          </Grid>
                          <Grid xs={12} md={6}>
                            <TextField
                              fullWidth
                              disabled
                              label="Date of Birth"
                              // value={`${provider.dateOfBirth ? provider.dateOfBirth : 'N/A'}`}
                              value="Date of Birth"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: theme.palette.grey[50],
                                  borderRadius: 2,
                                  '&.Mui-disabled': {
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: theme.palette.divider,
                                    },
                                    '& .MuiInputLabel-root': {
                                      color: theme.palette.text.secondary,
                                    },
                                  },
                                },
                                '& .MuiInputLabel-root': {
                                  color: theme.palette.text.primary,
                                },
                              }}
                            />
                          </Grid>
                          <Grid xs={12} md={6}>
                            <TextField
                              fullWidth
                              disabled
                              label="Zip Code"
                              // value={`${provider.zipCode ? provider.zipCode : 'N/A'}`}
                              value='Zip Code'
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: theme.palette.grey[50],
                                  borderRadius: 2,
                                  '&.Mui-disabled': {
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: theme.palette.divider,
                                    },
                                    '& .MuiInputLabel-root': {
                                      color: theme.palette.text.secondary,
                                    },
                                  },
                                },
                                '& .MuiInputLabel-root': {
                                  color: theme.palette.text.primary,
                                },
                              }}
                            />
                          </Grid>
                          <Grid xs={12}>
                            <TextField
                              fullWidth
                              multiline
                              disabled
                              label="Bio"
                              value={`${provider.profile_bio ? provider.profile_bio : 'N/A'}`}
                              // value="Bio info here"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: theme.palette.grey[50],
                                  borderRadius: 2,
                                  '&.Mui-disabled': {
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: theme.palette.divider,
                                    },
                                    '& .MuiInputLabel-root': {
                                      color: theme.palette.text.secondary,
                                    },
                                  },
                                },
                                '& .MuiInputLabel-root': {
                                  color: theme.palette.text.primary,
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h6">Provider Details</Typography>
                        <Box onClick={(e) => e.stopPropagation()}>
                          <QuestionMarkInfoPopup message="The provider details section contains information that the clinician has provided about themselves. Any solid non-red boxes represent overlaps between the client's preferences and the clinician's profile." />
                        </Box>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={3}>
                        <Grid xs={12} sm={6}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              height: '100%',
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 2,
                              transition: 'transform 0.2s',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: theme.shadows[4],
                              },
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                              <PeopleIcon color="primary" />
                              <Typography variant="h6">Demographics</Typography>
                            </Stack>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {providerDemographics.map((demographic, index) => (
                                <Chip
                                  key={index}
                                  label={demographic}
                                  color={selectedDemographics.includes(demographic) ? "primary" : "default"}
                                  variant={selectedDemographics.includes(demographic) ? "filled" : "outlined"}
                                  sx={{
                                    borderRadius: 1.5,
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                      transform: 'scale(1.05)',
                                    },
                                  }}
                                />
                              ))
                              }
                              {selectedDemographics.map((option) => (
                                !providerDemographics.includes(option) && (
                                  <Chip
                                    key={option}
                                    label={option}
                                    color="error"
                                    sx={{
                                      borderRadius: 1.5,
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        transform: 'scale(1.05)',
                                      },
                                    }}
                                  />
                                )
                              ))
                              }
                              {
                                selectedDemographics.length === 0 && providerDemographics.length === 0 && <Typography color="text.secondary">None</Typography>
                              }
                            </Box>
                          </Paper>
                        </Grid>
                        <Grid xs={12} sm={6}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              height: '100%',
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 2,
                              transition: 'transform 0.2s',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: theme.shadows[4],
                              },
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                              <PsychologyIcon color="primary" />
                              <Typography variant="h6">Specialties</Typography>
                            </Stack>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {provider.clinical_specialties?.map((specialty, index) => (
                                <Chip
                                  key={index}
                                  label={specialty}
                                  color={selectedSpecialties.includes(specialty) ? "primary" : "default"}
                                  variant={selectedSpecialties.includes(specialty) ? "filled" : "outlined"}
                                  sx={{
                                    borderRadius: 1.5,
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                      transform: 'scale(1.05)',
                                    },
                                  }}
                                />
                              ))
                              }
                              {selectedSpecialties.map((option) => (
                                !provider.clinical_specialties?.includes(option) && (
                                  <Chip
                                    key={option}
                                    label={option}
                                    color="error"
                                    sx={{
                                      borderRadius: 1.5,
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        transform: 'scale(1.05)',
                                      },
                                    }}
                                  />
                                )
                              ))
                              }
                              {
                                selectedSpecialties.length === 0 && provider.clinical_specialties?.length === 0 && <Typography color="text.secondary">None</Typography>
                              }
                            </Box>
                          </Paper>
                        </Grid>
                        <Grid xs={12} sm={6}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              height: '100%',
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 2,
                              transition: 'transform 0.2s',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: theme.shadows[4],
                              },
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                              <TranslateIcon color="primary" />
                              <Typography variant="h6">Languages Spoken</Typography>
                            </Stack>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {provider.languages_spoken && provider.languages_spoken.length !== 0 ? (
                                provider.languages_spoken.map((item, index) => (
                                  <Chip
                                    key={index}
                                    label={item}
                                    variant="outlined"
                                    sx={{
                                      borderRadius: 1.5,
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        transform: 'scale(1.05)',
                                      },
                                    }}
                                  />
                                ))
                              ) : (
                                <Typography color="text.secondary">N/A</Typography>
                              )}
                            </Box>
                          </Paper>
                        </Grid>
                        <Grid xs={12} sm={6}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              height: '100%',
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 2,
                              transition: 'transform 0.2s',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: theme.shadows[4],
                              },
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                              <LocationOnIcon color="primary" />
                              <Typography variant="h6">In-person Locations</Typography>
                            </Stack>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {provider.in_person_locations && provider.in_person_locations.length !== 0 ? (
                                provider.in_person_locations.map((item, index) => (
                                  <Chip
                                    key={index}
                                    label={item}
                                    variant="outlined"
                                    sx={{
                                      borderRadius: 1.5,
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        transform: 'scale(1.05)',
                                      },
                                    }}
                                  />
                                ))
                              ) : (
                                <Typography color="text.secondary">None</Typography>
                              )}
                            </Box>
                          </Paper>
                        </Grid>
                        <Grid xs={12} sm={6}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              height: '100%',
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 2,
                              transition: 'transform 0.2s',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: theme.shadows[4],
                              },
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                              <CakeIcon color="primary" />
                              <Typography variant="h6">Preferred Patient Age Groups</Typography>
                            </Stack>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {provider.preferred_patient_age_groups && provider.preferred_patient_age_groups.length !== 0 ? (
                                provider.preferred_patient_age_groups.map((ageGroup, index) => (
                                  <Chip
                                    key={index}
                                    label={ageGroup}
                                    variant="outlined"
                                    sx={{
                                      borderRadius: 1.5,
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        transform: 'scale(1.05)',
                                      },
                                    }}
                                  />
                                ))
                              ) : (
                                <Typography color="text.secondary">No Preference</Typography>
                              )}
                            </Box>
                          </Paper>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h6">Open Referral requests</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <ReferralCards providerID={provider.id} clientID={clientID} closeModal={displayModal.onFalse} state={selectedState} />
                    </AccordionDetails>
                  </Accordion>

                </Box>
                <Stack>
                  <FormCommentsSection parentId={clientID} parentType='client' />
                </Stack>
              </Stack>
            </DialogContent>
            :
            <>
            </>
        }
        <DialogActions sx={{ heigth: '50px' }}>
          <Button variant="outlined" onClick={displayModal.onFalse}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
